import React from 'react';
import { connect } from 'react-redux';
import { Prompt } from 'react-router-dom';
import { Button, Spinner, Tab } from 'react-bootstrap';
import Select from 'react-select';
import { Form } from './Form';
import CardHeader from '../Card/CardHeader';
import { UnsavedChangesGuard } from './UnsavedChangesGuard';
import { isPanelCountry } from './surveyConstants';
import { childProjectNameParts, composeChildProjectName, deriveChildProjectSuffix, normalizeChildProjectName } from './childProjectName';
import { CreateStepper, CreateStep } from './redesign/CreateStepper';
import { MarketList } from './redesign/MarketList';
import { isDirtyingEvent } from './redesign/dirtyTracking';
import { statusLabel } from './redesign/surveyMetrics';
import { projectReviewRows, marketReviewRows, totalQuota } from './redesign/createReview';
import './redesign/surveys-redesign.css';

// Form's exported type (withRouter(connect(reduxForm))) drops its own props;
// same loose typing as SurveyDetailPage.
const SetupForm: React.ComponentType<any> = Form as any;

export const LEAVE_MESSAGE = 'This survey has not been created yet. Leave and lose what you entered?';
const STATUS_HINT = '(initial status; cascades to all markets)';
export const LAUNCH_NOTE = 'SMS/WhatsApp name, points and send settings are set after the survey is created, on the Schedule tab.';

// What Form passes to renderLayout (Form.layoutParts).
type LayoutParts = {
  panes: React.ReactNode;
  state: any;
  markets: any[];
  onCountryChange: (selected: any[]) => void;
  onChildFieldChange: (childKey: string, field: string, value: any) => void;
  submitting: boolean;
  canSubmit: boolean;
};

interface Props {
  role: string;
  history: { push: (path: string) => void };
}

interface State {
  step: CreateStep;
  visited: CreateStep[];
  // Selected market on the Markets step: a market key, or the nonce of a market
  // just added (its key exists only after Form's state update).
  marketKey: string | null;
  addedNonce: string | null;
  adding: boolean;
  dirty: boolean;
  created: boolean;
}

// Same shape as the nonce Form stamps on a newly picked country (handleCountryChange
// keeps an existing nonce), so the page can find the market it just added.
const newNonce = (shortName: string) => `${shortName}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

class SurveyCreatePageComponent extends React.Component<Props, State> {
  state: State = {
    step: 1,
    visited: [1],
    marketKey: null,
    addedNonce: null,
    adding: false,
    dirty: false,
    created: false,
  };

  goTo = (step: CreateStep) => {
    this.setState((p) => ({ step, adding: false, visited: p.visited.includes(step) ? p.visited : [...p.visited, step] }));
    const top = document.querySelector('.dx-create');
    if (top && typeof (top as any).scrollIntoView === 'function') (top as any).scrollIntoView({ block: 'start' });
  };

  cancel = () => {
    // The Prompt below asks first when something was entered.
    this.props.history.push(`/${this.props.role}/surveys`);
  };

  onCreated = (id: string) => {
    // Turn the leave prompt off before leaving for the new survey.
    this.setState({ created: true }, () => {
      this.props.history.push(`/${this.props.role}/surveys/details/${id}?tab=overview`);
    });
  };

  markDirty = (e: React.SyntheticEvent) => {
    if (!this.state.dirty && isDirtyingEvent(e.target, e.type)) this.setState({ dirty: true });
  };

  selectedMarket = (markets: any[]) => {
    const { marketKey, addedNonce } = this.state;
    return (addedNonce && markets.find((m) => m.option && m.option.nonce === addedNonce))
      || (marketKey && markets.find((m) => m.childKey === marketKey))
      || markets[0];
  };

  // "+ Add market" and "+ Add another <code>": the same handler as the Countries
  // multi-select (Form.handleCountryChange), with the new option appended.
  addMarket = (parts: LayoutParts, country: any) => {
    if (!country) return;
    const option = { ...country, nonce: newNonce(country.shortName) };
    parts.onCountryChange([...(parts.state.selectedCountryOption || []), option]);
    this.setState({ adding: false, addedNonce: option.nonce, marketKey: null, dirty: true });
  };

  removeMarket = (parts: LayoutParts, market: any) => {
    parts.onCountryChange((parts.state.selectedCountryOption || []).filter((o: any) => o !== market.option));
    this.setState({ addedNonce: null, marketKey: null, dirty: true });
  };

  renderMarketList = (parts: LayoutParts, selected: any) => {
    const { state, markets } = parts;
    const childStates = state.childStates || {};
    const items = markets.map((m) => ({
      id: m.childKey,
      shortName: m.shortName,
      country: m.countryName,
      surveyType: (childStates[m.childKey] || {}).surveyType || state.surveyType,
    }));
    return (
      <div className="dx-card dx-market-list">
        <div className="d-flex justify-content-between align-items-baseline mb-2">
          <h6 className="mb-0">Markets ({markets.length})</h6>
          <span className="text-muted small">Quota {totalQuota(state, markets)}</span>
        </div>
        <MarketList
          markets={items}
          selectedId={selected ? selected.childKey : ''}
          onSelect={(id) => this.setState({ marketKey: id, addedNonce: null })}
          renderRowAction={(m) => {
            const country = (state.countries || []).find((c: any) => c.shortName === m.shortName);
            if (!country) return null;
            return (
              <button
                type="button"
                className="btn btn-link btn-sm p-0 dx-add-another"
                onClick={(e) => { e.stopPropagation(); this.addMarket(parts, country); }}
              >
                + Add another {m.shortName}
              </button>
            );
          }}
        />
        {this.state.adding ? (
          <div className="mt-2">
            <Select
              id="countryTitle"
              name="countryTitle"
              classNamePrefix="dx-select"
              aria-label="Choose a country to add"
              placeholder="Search country..."
              autoFocus
              defaultMenuIsOpen
              options={state.countries || []}
              value={null}
              onChange={(opt: any) => this.addMarket(parts, opt)}
              onBlur={() => this.setState({ adding: false })}
            />
          </div>
        ) : (
          <Button variant="outline-primary" size="sm" block className="mt-2" onClick={() => this.setState({ adding: true })}>
            + Add market
          </Button>
        )}
      </div>
    );
  };

  // Client Quota and Child Project Name for the selected market: in create mode
  // they live on Project Setup's per-country rows, which step 1 hides. Same
  // input names, same state writes (Form.updateChildField) as those rows.
  renderMarketHead = (parts: LayoutParts, m: any) => {
    const { state, onChildFieldChange } = parts;
    const c = (state.childStates || {})[m.childKey] || {};
    const sn = m.shortName || '';
    const parentName = state.parentProjectName || state.projectName || '';
    const { prefix, base } = childProjectNameParts(parentName, sn);
    const title = normalizeChildProjectName(c.childProjectName, parentName, sn) || base;
    const quota = c.userLimitCommitted;
    const isError = state.surveyType === 'Error' || c.surveyType === 'Error';
    const quotaInvalid = !isError && (quota === undefined || quota === null || quota === ''
      || !Number.isFinite(Number(quota)) || Number(quota) < 1);
    const quotaId = `userLimitCommitted-${m.childKey}`;
    const nameId = `childProjectName-${m.childKey}`;
    return (
      <div className="dx-market-head">
        <div className="d-flex flex-wrap justify-content-between align-items-start mb-2">
          <div>
            <h5 className="mb-0">{title}</h5>
            <div className="text-muted small">
              {m.countryName} ({sn}) · {isPanelCountry(sn) ? 'DataXing panel market' : 'external market'}
            </div>
          </div>
          <div className="small">Status: <strong>{statusLabel(c.surveyType || state.surveyType)}</strong></div>
        </div>
        <div className="row">
          <div className="col-md-6">
            <label htmlFor={quotaId}>Client Quota*</label>
            <input
              type="number"
              min={1}
              id={quotaId}
              name={quotaId}
              className={`form-control${quotaInvalid ? ' is-invalid' : ''}`}
              value={quota ?? ''}
              onChange={(e) => onChildFieldChange(m.childKey, 'userLimitCommitted', e.target.value === '' ? '' : Number(e.target.value))}
            />
            {quotaInvalid && <small className="text-danger d-block">Required, minimum 1.</small>}
          </div>
          <div className="col-md-6">
            <label htmlFor={nameId}>Child Project Name</label>
            <div className="input-group">
              <span className="input-group-text" style={{ userSelect: 'none' }}>{prefix}</span>
              <input
                type="text"
                id={nameId}
                name={nameId}
                className="form-control"
                placeholder="optional suffix"
                value={deriveChildProjectSuffix(c.childProjectName || '', parentName, sn)}
                onChange={(e) => onChildFieldChange(m.childKey, 'childProjectName', composeChildProjectName(parentName, sn, e.target.value))}
              />
            </div>
          </div>
        </div>
        <hr />
      </div>
    );
  };

  renderReview = (parts: LayoutParts) => {
    const { state, markets } = parts;
    const rows = marketReviewRows(state, markets);
    return (
      <div className="dx-review">
        <div className="dx-card">
          <div className="d-flex justify-content-between align-items-baseline mb-2">
            <h6 className="mb-0">Project</h6>
            <button type="button" className="btn btn-link btn-sm p-0" onClick={() => this.goTo(1)}>Edit</button>
          </div>
          <div className="row">
            {projectReviewRows(state).map((r) => (
              <p key={r.label} className="col-md-6 mb-2"><strong>{r.label}:</strong> {r.value}</p>
            ))}
          </div>
        </div>
        <div className="dx-card">
          <div className="d-flex justify-content-between align-items-baseline mb-2">
            <h6 className="mb-0">Markets ({markets.length}) · total quota {totalQuota(state, markets)}</h6>
            <button type="button" className="btn btn-link btn-sm p-0" onClick={() => this.goTo(2)}>Edit</button>
          </div>
          <div className="table-responsive">
            <table className="table table-sm table-bordered dx-table mb-0">
              <thead className="thead-light">
                <tr>
                  <th>Market</th>
                  <th>Child project</th>
                  <th>Quota</th>
                  <th>Survey links</th>
                  <th>Pre-screen</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan={5} className="text-muted">No markets yet.</td></tr>
                )}
                {rows.map((r) => (
                  <tr key={r.key}>
                    <td><strong>{r.market}</strong> {r.country}</td>
                    <td>{r.childProject}</td>
                    <td>{r.quota}</td>
                    <td>{r.links}</td>
                    <td>{r.prescreen}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="alert alert-info">
          Partners are added after the survey is created, from the survey's <strong>Partners</strong> tab.
        </div>
      </div>
    );
  };

  renderBar = (parts: LayoutParts) => {
    const { step } = this.state;
    // Distinct keys: "Next" and "Create survey" must never be the same DOM
    // button, or the click on Next that opens step 3 would submit the form.
    return (
      <div className="dx-step-nav">
        <button type="button" className="btn btn-link px-0" onClick={this.cancel}>Cancel</button>
        <div className="d-flex align-items-center">
          {parts.submitting && <Spinner animation="border" variant="primary" size="sm" className="mr-2" />}
          {step > 1 && (
            <button key="back" type="button" className="btn btn-light mr-2" onClick={() => this.goTo((step - 1) as CreateStep)}>
              ← Back
            </button>
          )}
          {step < 3 ? (
            <button key="next" type="button" className="btn btn-primary" onClick={() => this.goTo((step + 1) as CreateStep)}>
              Next →
            </button>
          ) : (
            // Runs Form's own submit (handleSubmit -> onSubmit -> create()).
            <button key="create" type="submit" className="btn btn-primary" disabled={!parts.canSubmit}>
              Create survey
            </button>
          )}
        </div>
      </div>
    );
  };

  // Called by Form inside its <form>. The panes element keeps the same position
  // on every step (wrappers only change classes), so Form's panes never remount.
  renderLayout = (parts: LayoutParts) => {
    const { step } = this.state;
    const selected = this.selectedMarket(parts.markets);
    const onMarkets = step === 2;
    const activeKey = step === 1 ? 'parent' : (selected ? selected.key : 'none');
    const hidePanes = step === 3 || (onMarkets && !selected);
    return (
      <>
        <div className={onMarkets ? 'row' : undefined}>
          {onMarkets ? <div className="col-md-4 col-xl-3">{this.renderMarketList(parts, selected)}</div> : null}
          <div className={onMarkets ? 'col-md-8 col-xl-9' : undefined}>
            {onMarkets && !selected ? <div className="dx-card text-muted">Add a market to configure it.</div> : null}
            <div className={`dx-card${hidePanes ? ' d-none' : ''}`}>
              {onMarkets && selected ? this.renderMarketHead(parts, selected) : null}
              <Tab.Container activeKey={activeKey}>{parts.panes}</Tab.Container>
              {onMarkets && selected ? (
                <div className="dx-danger-zone d-flex flex-wrap justify-content-between align-items-center mt-3">
                  <div>
                    <strong>Remove this market</strong>
                    <div className="text-muted small">Not saved yet; removes it from this draft.</div>
                  </div>
                  <button type="button" className="btn btn-outline-danger btn-sm" onClick={() => this.removeMarket(parts, selected)}>
                    Remove market
                  </button>
                </div>
              ) : null}
            </div>
            {step === 3 ? this.renderReview(parts) : null}
          </div>
        </div>
        {this.renderBar(parts)}
      </>
    );
  };

  render() {
    const { role } = this.props;
    const { step, visited, dirty, created } = this.state;
    const guard = dirty && !created;
    return (
      <div className="dx-surveys dx-create">
        <nav className="dx-breadcrumb">
          <a href={`#/${role}/surveys`}>Surveys</a>
          {' › '}
          <span>Create survey</span>
        </nav>

        <div className="w-100 mb-3">
          <CardHeader color="primary">
            <h4 className="mb-0">Create Survey</h4>
          </CardHeader>
        </div>

        <CreateStepper current={step} visited={visited} onSelect={this.goTo} />

        {/* Bubble phase on purpose. React 17 runs capture handlers in their own
            dispatch, so a setState there re-renders the controlled field back
            to its old value before the field's onChange runs, and the first
            change (the one that sets dirty) is lost. */}
        <div onChange={this.markDirty} onInput={this.markDirty} onClick={this.markDirty}>
          <SetupForm
            inline
            show
            hideTabNav
            role={role}
            statusHint={STATUS_HINT}
            hideMarketFields
            // Panel launch settings are not shown here, only on the survey's
            // Schedule tab; create() still sends their defaults from Form state.
            childFieldGroup="market"
            childLaunchNote={LAUNCH_NOTE}
            // Status is set once in step 1 and cascades to every market, so
            // step 2 has no per-market Survey Status radios. create() still
            // sends it (hideSurveyStatus, which drops it, stays off here).
            hideMarketStatus
            // A new function on every page render on purpose: Form is wrapped in
            // connect/reduxForm, which skip re-rendering on unchanged props, and
            // the layout must follow this page's step and market selection.
            renderLayout={(parts: LayoutParts) => this.renderLayout(parts)}
            onCreated={this.onCreated}
            onClose={this.cancel}
          />
        </div>

        <Prompt when={guard} message={LEAVE_MESSAGE} />
        <UnsavedChangesGuard isDirty={guard} />
      </div>
    );
  }
}

const mapStateToProps = (state: any) => ({
  role: state.adminUser?.adminUser?.role || 'admin',
});

export { SurveyCreatePageComponent };
export const SurveyCreatePage = connect(mapStateToProps)(SurveyCreatePageComponent as any);
export default SurveyCreatePage;
